<template>
  <section id="wallet-page">
    <!-- Mobile Header -->
    <div class="custom-mobile-header d-md-none">
      <div class="header-content">
        <button type="button" class="back-button" @click="$router.go(-1)">
          <v-icon size="25" color="#fff">mdi-arrow-right-bold</v-icon>
        </button>
        <div class="header-title">کیف پول</div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
    </div>

    <div v-else class="wallet-wrap">
      <div class="page-top d-none d-md-block">
        <h3 class="mb-0">کیف پول</h3>
        <p class="text-muted mb-0">کیف پول را شارژ کنید تا تمدیدِ بعدی با یک کلیک انجام شود.</p>
      </div>

      <div class="hero">
        <v-icon size="44" color="#fff" class="hero-icon">mdi-wallet</v-icon>
        <div class="hero-text">
          <div class="lbl">موجودی قابل استفاده</div>
          <div class="bal">{{ toman(balance) }} تومان</div>
          <div v-if="transactions.length" class="lbl">آخرین تغییر: {{ date(transactions[0].createdAt) }} — {{ typeText(transactions[0].type) }}</div>
        </div>
        <div class="hero-acts">
          <b-button variant="light" @click="openTopup">+ افزایش موجودی</b-button>
          <b-button variant="light" @click="$router.push({ name: 'subscription-renew' })">تمدید با کیف پول</b-button>
        </div>
      </div>

      <div class="grid">
        <div class="section-card">
          <div class="section-header">
            <v-icon size="20" color="#0f172a">mdi-format-list-bulleted</v-icon>
            <h5 class="mb-0">گردش حساب</h5>
          </div>
          <div class="section-body">
            <div v-if="!transactions.length" class="text-center text-muted py-4">
              هنوز گردشی ثبت نشده است
            </div>
            <div v-for="tx in transactions" :key="tx._id" class="tx-row">
              <div class="tx-icon" :class="tx.amount > 0 ? 'in' : 'out'">
                <v-icon size="18" color="#fff">{{ tx.amount > 0 ? 'mdi-arrow-down' : 'mdi-arrow-up' }}</v-icon>
              </div>
              <div class="tx-main">
                <div class="tx-desc">{{ tx.description || typeText(tx.type) }}</div>
                <div class="tx-meta">
                  {{ date(tx.createdAt) }}
                  <span v-if="tx.ref"> — کد پیگیری <span class="ltr">{{ tx.ref }}</span></span>
                </div>
              </div>
              <div class="tx-amt">
                <div :class="tx.amount > 0 ? 'plus' : 'minus'">{{ tx.amount > 0 ? '+' : '−' }}{{ toman(Math.abs(tx.amount)) }}</div>
                <div class="tx-meta">مانده {{ toman(tx.balanceAfter) }}</div>
              </div>
            </div>
            <div v-if="total > transactions.length" class="text-center mt-2">
              <b-button variant="link" size="sm" :disabled="moreLoading" @click="loadMore">
                <b-spinner v-if="moreLoading" small /> نمایش بیشتر
              </b-button>
            </div>
          </div>
        </div>

        <div ref="topupCard" class="section-card topup-card">
          <div class="section-header">
            <v-icon size="20" color="#0f172a">mdi-plus-circle</v-icon>
            <h5 class="mb-0">افزایش موجودی</h5>
          </div>
          <div class="section-body">
            <template v-if="gatewayEnabled">
              <div class="text-muted small">مبلغ را انتخاب یا وارد کنید</div>
              <div class="presets">
                <button
                  v-for="p in presets"
                  :key="p"
                  type="button"
                  :class="{ sel: amount === p }"
                  @click="amount = p"
                >
                  {{ shortToman(p) }}
                </button>
              </div>
              <div class="amount-input">
                <input
                  :value="amountText"
                  inputmode="numeric"
                  placeholder="مبلغ"
                  @input="onAmount($event.target.value)"
                >
                <span class="text-muted">تومان</span>
              </div>
              <div class="small mt-1" :class="amountError ? 'text-danger' : 'text-muted'">
                {{ amountError || 'حداقل ۵۰ هزار و حداکثر ۵۰ میلیون تومان' }}
              </div>
              <b-button variant="success" block class="mt-3 pay-btn" :disabled="Boolean(amountError) || !amount || paying" @click="topup">
                <b-spinner v-if="paying" small class="ml-1" />
                پرداخت {{ amount ? fa(group(amount)) : '' }} تومان
              </b-button>
              <div class="secure">
                <v-icon size="16" color="#64748b">mdi-lock</v-icon>
                درگاه بانک ملی (سداد)
              </div>
            </template>
            <div v-else class="text-center text-muted py-3">
              درگاه بانکی موقتاً غیرفعال است؛ شارژ کیف پول فعلاً ممکن نیست.
            </div>
            <hr>
            <p class="text-muted small mb-0">
              موجودی کیف پول فقط برای تمدید و خرید در چهارراه است و نقد نمی‌شود؛ برای بازگشت وجه با پشتیبانی تماس بگیرید.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { BButton, BSpinner } from 'bootstrap-vue'
import PaymentService from '@/services/payment.service'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const MIN = 50000
const MAX = 50000000

export default {
  components: { BButton, BSpinner },
  data() {
    return {
      loading: true,
      moreLoading: false,
      paying: false,
      balance: 0,
      gatewayEnabled: false,
      transactions: [],
      total: 0,
      page: 1,
      amount: 500000,
      presets: [200000, 500000, 750000, 1000000],
    }
  },
  computed: {
    amountText() {
      return this.amount ? this.fa(this.group(this.amount)) : ''
    },
    amountError() {
      if (!this.amount) return ''
      if (this.amount < MIN) return 'حداقل مبلغ ۵۰ هزار تومان است'
      if (this.amount > MAX) return 'حداکثر مبلغ ۵۰ میلیون تومان است'
      return ''
    },
  },
  async mounted() {
    const wanted = Number(this.$route.query.topup)
    if (wanted > 0) this.amount = Math.max(MIN, Math.ceil(wanted / 1000) * 1000)
    await this.load()
    if (wanted > 0) this.openTopup()
  },
  methods: {
    async load() {
      try {
        const res = await PaymentService.getWallet()
        if (res.status !== 'success') throw new Error(res.message)
        this.balance = res.result.balance
        this.gatewayEnabled = res.result.gatewayEnabled
        this.transactions = res.result.transactions
        this.total = res.result.total
        this.page = 1
      } catch (error) {
        this.toast('خطا', error.message || 'خطا در دریافت کیف پول', 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadMore() {
      this.moreLoading = true
      try {
        const res = await PaymentService.getWalletTransactions({ page: this.page + 1, limit: 10 })
        if (res.status === 'success') {
          this.page += 1
          this.transactions = [...this.transactions, ...res.result.rows]
        }
      } finally {
        this.moreLoading = false
      }
    },
    openTopup() {
      this.$nextTick(() => {
        if (this.$refs.topupCard) this.$refs.topupCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    onAmount(text) {
      const digits = String(text).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[^\d]/g, '')
      this.amount = digits ? Math.min(Number(digits), 999999999) : 0
      this.$forceUpdate()
    },
    async topup() {
      this.paying = true
      try {
        const res = await PaymentService.topupWallet({ amount: this.amount })
        if (res.status !== 'success') throw new Error(res.message)
        window.location.href = res.result.paymentUrl
      } catch (error) {
        this.toast('شارژ انجام نشد', error.message || 'خطا در اتصال به درگاه', 'danger')
        this.paying = false
      }
    },
    typeText(type) {
      return {
        topup: 'شارژ از درگاه', purchase: 'تمدید اشتراک', refund: 'بازگشت وجه', adjust: 'اصلاح توسط پشتیبانی',
      }[type] || type
    },
    date(value) {
      const d = new Date(value)
      return `${d.toLocaleDateString('fa-IR')} — ${d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}`
    },
    toman(rial) {
      return new Intl.NumberFormat('fa-IR').format(Math.round((rial || 0) / 10))
    },
    group(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
    },
    shortToman(t) {
      if (t >= 1000000) return `${new Intl.NumberFormat('fa-IR').format(t / 1000000)} میلیون`
      return `${new Intl.NumberFormat('fa-IR').format(t / 1000)} هزار`
    },
    fa(value) {
      return String(value).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },
    toast(title, text, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title, text, icon: variant === 'danger' ? 'XIcon' : 'CheckIcon', variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.wallet-wrap {
  max-width: 1200px;
  margin: 0 auto;
}

.page-top {
  margin-bottom: 18px;
}

.hero {
  background: linear-gradient(135deg, #0284c7, #0ea5e9 55%, #38bdf8);
  color: #fff;
  border-radius: 18px;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 18px;
  position: relative;
  overflow: hidden;
}

.hero::after {
  content: "";
  position: absolute;
  left: -40px;
  bottom: -70px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.hero-text {
  z-index: 1;
}

.hero .bal {
  font-size: 30px;
  font-weight: 800;
}

.hero .lbl {
  opacity: 0.9;
  font-size: 13px;
}

.hero-acts {
  margin-right: auto;
  display: flex;
  gap: 8px;
  z-index: 1;
}

.hero-acts .btn {
  color: #0284c7;
  font-weight: 700;
  border-radius: 10px;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 18px;
  align-items: start;
}

.section-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  border-bottom: 1px solid #f1f5f9;
}

.section-body {
  padding: 16px 18px;
}

.tx-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}

.tx-row:last-of-type {
  border-bottom: 0;
}

.tx-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: grid;
  place-items: center;
  flex: none;
}

.tx-icon.in {
  background: #16a34a;
}

.tx-icon.out {
  background: #f97316;
}

.tx-main {
  min-width: 0;
  flex: 1;
}

.tx-desc {
  font-weight: 600;
  color: #0f172a;
}

.tx-meta {
  font-size: 12px;
  color: #64748b;
}

.tx-amt {
  text-align: left;
  white-space: nowrap;
}

.plus {
  color: #16a34a;
  font-weight: 800;
}

.minus {
  color: #dc2626;
  font-weight: 800;
}

.ltr {
  display: inline-block;
  direction: ltr;
  unicode-bidi: isolate;
}

.presets {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin: 10px 0;
}

.presets button {
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px;
  font-weight: 700;
  background: #fff;
  font-family: inherit;
  color: #0f172a;
}

.presets button.sel {
  border-color: #0ea5e9;
  background: #f0f9ff;
  color: #0284c7;
}

.amount-input {
  display: flex;
  align-items: center;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px;
  gap: 8px;
}

.amount-input input {
  border: 0;
  outline: 0;
  font-family: inherit;
  font-size: 17px;
  flex: 1;
  background: transparent;
  min-width: 0;
}

.pay-btn {
  padding: 11px;
  font-weight: 700;
  border-radius: 12px;
}

.secure {
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 12px;
  margin-top: 10px;
}

.custom-mobile-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  z-index: 999;
  display: flex;
  align-items: center;
  padding: 0 15px;
}

.custom-mobile-header .header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.custom-mobile-header .back-button {
  position: absolute;
  right: 0;
  background: none;
  border: none;
  padding: 5px;
}

.custom-mobile-header .header-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
}

@media (max-width: 991px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .topup-card {
    order: -1;
  }
}

@media (max-width: 768px) {
  #wallet-page {
    margin: -28px -28px 0;
    padding: 72px 12px 24px;
  }

  .hero {
    flex-wrap: wrap;
    padding: 18px;
  }

  .hero-icon {
    display: none !important;
  }

  .hero .bal {
    font-size: 24px;
  }

  .hero-acts {
    margin-right: 0;
    width: 100%;
  }

  .hero-acts .btn {
    flex: 1;
    font-size: 13px;
  }
}
</style>
